/* Export helpers for the org chart PDF. Pure functions, no DOM and no print
   dialog, so the scoping and fit decisions are unit-testable on their own. */

/* Printable width of A4 landscape at the 10mm margins declared in
   orgChartPrint.css: 297mm - 20mm = 277mm. At the CSS reference 96dpi that is
   277 / 25.4 * 96 = 1047px.

   Retained for the off-screen print document's width only. It is no longer a
   fit TEST: the print renders one section per manager with wrapping card rows
   (see orgChartSections.js), so no section can exceed the page width whatever
   the org's shape, and there is nothing left to measure or degrade. */
export const LANDSCAPE_CONTENT_PX = 1047;

/**
 * Scope a flat node list to one department (the `func` field), keeping the
 * REPORTING PATH from the root down to that department.
 *
 * Keeping only the matching nodes was wrong: a department's own manager usually
 * sits in a different department (a CTO in Technology owning Engineering teams),
 * so filtering on `func` alone orphaned every top node of the department and the
 * printed chart began mid-hierarchy with no route back to the top. A department
 * chart that does not say who the department reports to is not much use.
 *
 * So this keeps every matching node PLUS every ancestor of a matching node. The
 * ancestors are context: they are what makes the document read from the root
 * down. Nothing is promoted to a root artificially, because every kept node's
 * manager is kept by construction, so the reporting chain stays intact all the
 * way up.
 *
 * Non-matching SIBLINGS are still removed. Only the path matters, not the rest
 * of the org.
 *
 * @param {Array} nodes flat node list
 * @param {string} dept department (func) name, or 'all' for no scoping
 * @returns {Array} matching nodes plus their ancestor chain, links intact
 */
export function scopeToDepartment(nodes, dept) {
  if (!dept || dept === 'all') return nodes;

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const keep = new Set();

  for (const n of nodes) {
    if (n.func !== dept) continue;
    keep.add(n.id);
    // Walk up to the root, adding the path. `seen` guards against a cycle in the
    // data: buildNodes cuts cycles server side, but this must not hang if a
    // malformed payload ever reaches it.
    const seen = new Set([n.id]);
    let cur = n.managerId ? byId.get(n.managerId) : null;
    while (cur && !seen.has(cur.id)) {
      keep.add(cur.id);
      seen.add(cur.id);
      cur = cur.managerId ? byId.get(cur.managerId) : null;
    }
  }

  // Order is preserved from the input, so the tree builder sees nodes in the same
  // sequence it would have without scoping.
  return nodes.filter((n) => keep.has(n.id));
}

/**
 * Caption for the print header, so a PDF that has left the app still says what
 * it is: which scope, and how fresh the data was.
 *
 * @param {object} opts
 * @param {string} opts.dept department name or 'all'
 * @param {string[]} opts.orgs selected organisation names
 * @param {string} opts.synced formatted sync timestamp
 * @returns {string}
 */
export function printCaption({ dept, orgs = [], synced = '' }) {
  // A department print carries the reporting path down from the root, so the
  // caption says so: otherwise a reader sees people from other departments in a
  // chart labelled with one department's name and reasonably calls it a bug.
  const scope = !dept || dept === 'all'
    ? 'All departments'
    : `${dept}, with its reporting line from the top`;
  const where = orgs.length > 0 ? orgs.join(', ') : 'All organizations';
  const when = synced ? ` • data synced ${synced}` : '';
  return `${scope} • ${where}${when}`;
}
